import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Plus, X, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAPIClient } from '@/hooks/useAPIClient';
import { CreateExampleRequest } from '@/types/fewShot';
import { MachineMultiSelect } from '@/components/shared/MachineMultiSelect';

const formSchema = z.object({
  question: z.string().min(10, 'Frage muss mindestens 10 Zeichen lang sein').max(1000, 'Frage zu lang'),
  answer: z.string().min(10, 'Antwort muss mindestens 10 Zeichen lang sein').max(3000, 'Antwort zu lang'),
  explanation: z.string().optional(),
  machines: z.array(z.string()).optional(),
  submachine_part: z.string().optional(),
  error_code: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ExampleFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: Partial<FormData>;
  mode?: 'create' | 'edit';
}

export function ExampleForm({ onSuccess, onCancel, initialData, mode = 'create' }: ExampleFormProps) {
  const { toast } = useToast();
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();
  const [tagInput, setTagInput] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: initialData?.question || '',
      answer: initialData?.answer || '',
      explanation: initialData?.explanation || '',
      machines: initialData?.machines || [],
      submachine_part: initialData?.submachine_part || '',
      error_code: initialData?.error_code || '',
      tags: initialData?.tags || [],
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateExampleRequest) => apiClient.fewShot.createExample(data),
    onSuccess: async (result) => {
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['few-shot', 'examples'] });
      await queryClient.invalidateQueries({ queryKey: ['few-shot', 'stats'] });

      // Get stats and show consolidated success message
      const stats = await apiClient.fewShot.getStatistics();
      toast({
        title: 'Beispiel gespeichert',
        description: `${stats.total_examples} Beispiele in Datenbank`,
      });

      reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Erstellen fehlgeschlagen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const currentTags = watch('tags') || [];
  const explanation = watch('explanation') || '';
  const isBadExample = explanation.trim().length > 0;

  const onSubmit = (data: FormData) => {
    // Determine if it's a good or bad example based on explanation
    const isGoodExample = !data.explanation?.trim();

    const submitData: CreateExampleRequest = {
      question: data.question.trim(),
      answer: data.answer.trim(),
      is_good_example: isGoodExample,
      explanation: data.explanation?.trim() || undefined,
      machines: data.machines?.filter(m => m.trim()) || [],
      submachine_part: data.submachine_part?.trim() || undefined,
      error_code: data.error_code?.trim() || undefined,
      tags: data.tags?.filter(tag => tag.trim()) || [],
    };

    createMutation.mutate(submitData);
  };

  const addTag = () => {
    if (tagInput.trim() && !currentTags.includes(tagInput.trim())) {
      setValue('tags', [...currentTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', currentTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Question and Answer Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Question Input */}
        <div className="space-y-2">
          <Label htmlFor="question" className="text-sm font-medium">
            Frage <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="question"
            placeholder="Welche Frage könnten Benutzer stellen..."
            {...register('question')}
            rows={6}
            className={errors.question ? 'border-red-500' : ''}
          />
          {errors.question && (
            <p className="text-sm text-red-500">{errors.question.message}</p>
          )}
        </div>

        {/* Answer Input */}
        <div className="space-y-2">
          <Label htmlFor="answer" className="text-sm font-medium">
            Antwort <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="answer"
            placeholder="Geben Sie die ideale Antwort ein..."
            {...register('answer')}
            rows={6}
            className={errors.answer ? 'border-red-500' : ''}
          />
          {errors.answer && (
            <p className="text-sm text-red-500">{errors.answer.message}</p>
          )}
        </div>
      </div>

      {/* Why is this a bad example field */}
      <div className="space-y-2">
        <Label htmlFor="explanation" className="text-sm font-medium">
          <span className="text-red-700">Warum ist das ein schlechtes Beispiel?</span> <span>(Optional - wenn ausgefüllt, wird es als schlechtes Beispiel markiert)</span>
        </Label>
        <Textarea
          id="explanation"
          placeholder="Erklären Sie, warum diese Antwort vermieden werden sollte..."
          {...register('explanation')}
          rows={2}
          className={`${errors.explanation ? 'border-red-500' : ''} ${isBadExample ? 'border-red-300 bg-red-50' : ''}`}
        />
        {errors.explanation && (
          <p className="text-sm text-red-500">{errors.explanation.message}</p>
        )}
        {isBadExample && (
          <p className="text-sm text-red-600 flex items-center space-x-1">
            <AlertCircle className="h-4 w-4" />
            <span>Dieses Beispiel wird als schlechtes Beispiel gespeichert</span>
          </p>
        )}
      </div>

      {/* Metadata Fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="machines" className="text-sm font-medium">
            Maschinen
          </Label>
          <MachineMultiSelect
            value={watch('machines') || []}
            onChange={(value) => setValue('machines', value)}
            placeholder="Maschinen auswahlen..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="submachine_part" className="text-sm font-medium">
            Unterkomponente
          </Label>
          <Input
            id="submachine_part"
            placeholder="z.B. Station 1151"
            autoComplete="off"
            {...register('submachine_part')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="error_code" className="text-sm font-medium">
            HMI Fehlercode
          </Label>
          <Input
            id="error_code"
            placeholder="z.B. 1A01-GX.1"
            autoComplete="off"
            {...register('error_code')}
          />
        </div>
      </div>

      {/* Tags Input */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Stichworte</Label>
        <div className="flex space-x-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagKeyPress}
            placeholder="Stichwort hinzufügen..."
            autoComplete="off"
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTag}
            disabled={!tagInput.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Display current tags */}
        {currentTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {currentTags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="flex items-center space-x-1">
                <span>{tag}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTag(tag)}
                  className="h-auto p-0 hover:bg-transparent"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-between">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={createMutation.isPending}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Zurück zur Übersicht</span>
          </Button>
        )}
        <div className="flex space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => reset()}
            disabled={createMutation.isPending}
          >
            Zurücksetzen
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="min-w-[200px] bg-green-600 hover:bg-green-700"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird erstellt...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Zur Wissensdatenbank hinzufügen
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}